import urls from "../global/constants/UrlConstants";
import { getCallParams, getHeaderObject, makeCall } from "../utils/service";
import { store } from "../utils/store";

export type BroadcastFilters = {
  stage?: string;
  /** Multi-select stages (preferred over single `stage`) */
  stages?: string[];
  source?: string;
  status?: string;
  search?: string;
  /** filled | not_filled | empty = all */
  registrationStatus?: string;
  bulkJobIds?: string[];
  bulkSetNames?: string[];
  /** crm_match | file_only | registration | feedback */
  audienceMode?: "crm_match" | "file_only" | "registration" | "feedback";
  /** Form id when audience is registration / feedback submissions */
  formId?: string;
  submissionFormId?: string;
};

export type AudienceContact = {
  phone: string;
  name?: string;
  email?: string;
};

export async function previewMarketingLeads(body: {
  organizationId?: string;
  organizationIds?: string[];
  projectId?: string;
  projectIds?: string[];
  filters?: BroadcastFilters;
  leadIds?: string[];
  bulkJobIds?: string[];
  bulkSetNames?: string[];
  audienceContacts?: AudienceContact[];
  /** file_only = message XLSX phones without CRM lead match/create */
  audienceMode?: "crm_match" | "file_only" | "registration" | "feedback";
  page?: number;
  pageSize?: number;
}) {
  const callParams = await getCallParams("POST", body);
  return makeCall(`${urls.url_prod}/whatsapp-marketing/preview-leads`, callParams);
}

export async function listWhatsAppAudienceForms(params: {
  organizationId?: string;
  organizationIds?: string[];
  formType: "registration" | "feedback";
}) {
  const qs = new URLSearchParams();
  if (params.organizationId) qs.set("organizationId", params.organizationId);
  if (params.organizationIds?.length) {
    qs.set("organizationIds", JSON.stringify(params.organizationIds));
  }
  qs.set("formType", params.formType);
  const callParams = await getCallParams("GET");
  return makeCall(
    `${urls.url_prod}/whatsapp-marketing/audience-forms?${qs.toString()}`,
    callParams
  );
}

export async function parseWhatsAppAudienceFile(params: {
  file: File;
  organizationIds?: string[];
  projectIds?: string[];
}) {
  const accessToken = store.getState().auth.accessToken;
  const headers: any = await getHeaderObject(accessToken, {});
  delete headers["Content-Type"];
  const fd = new FormData();
  fd.append("file", params.file);
  if (params.organizationIds?.length) {
    fd.append("organizationIds", JSON.stringify(params.organizationIds));
  }
  if (params.projectIds?.length) {
    fd.append("projectIds", JSON.stringify(params.projectIds));
  }
  const res = await fetch(`${urls.url_prod}/whatsapp-marketing/parse-audience`, {
    method: "POST",
    headers,
    body: fd,
  });
  const json = await res.json();
  if (!res.ok || json.success === 0) {
    throw new Error(json.message || json.error_msg || "Failed to parse file");
  }
  return json;
}

export async function createWhatsAppBroadcast(body: {
  name: string;
  organizationId?: string;
  organizationIds?: string[];
  projectId?: string;
  projectIds?: string[];
  templateWid: string;
  templateName?: string;
  templateMode?: "single" | "by_registration";
  templateWidRegistered?: string;
  templateNameRegistered?: string;
  leadIds?: string[];
  bulkJobIds?: string[];
  bulkSetNames?: string[];
  audienceContacts?: AudienceContact[];
  /** file_only = send to file phones; never creates CRM leads */
  audienceMode?: "crm_match" | "file_only" | "registration" | "feedback";
  filters?: BroadcastFilters;
  maxRetries?: number;
  /** Default: instant */
  runMode?: "instant" | "scheduled";
  /** Local IST wall time `YYYY-MM-DDTHH:mm` (no Z) */
  scheduledAt?: string;
  timezone?: string;
}) {
  const callParams = await getCallParams("POST", body);
  return makeCall(`${urls.url_prod}/whatsapp-marketing/broadcasts`, callParams);
}

export async function listWhatsAppBroadcasts(params: {
  organizationId?: string;
  projectId?: string;
  page?: number;
  pageSize?: number;
  search?: string;
}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && String(v) !== "") {
      qs.set(k, String(v));
    }
  });
  const callParams = await getCallParams("GET");
  return makeCall(
    `${urls.url_prod}/whatsapp-marketing/broadcasts?${qs.toString()}`,
    callParams
  );
}

export async function getWhatsAppBroadcast(id: string) {
  const callParams = await getCallParams("GET");
  return makeCall(
    `${urls.url_prod}/whatsapp-marketing/broadcasts/${id}`,
    callParams
  );
}

export async function getWhatsAppBroadcastProgress(id: string) {
  const callParams = await getCallParams("GET");
  return makeCall(
    `${urls.url_prod}/whatsapp-marketing/broadcasts/${id}/progress`,
    callParams
  );
}

export async function listWhatsAppBroadcastRecipients(
  id: string,
  params: {
    page?: number;
    pageSize?: number;
    status?: string;
    search?: string;
  } = {}
) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && String(v) !== "") {
      qs.set(k, String(v));
    }
  });
  const callParams = await getCallParams("GET");
  return makeCall(
    `${urls.url_prod}/whatsapp-marketing/broadcasts/${id}/recipients?${qs.toString()}`,
    callParams
  );
}

export async function retryFailedWhatsAppBroadcast(id: string) {
  const callParams = await getCallParams("POST", {});
  return makeCall(
    `${urls.url_prod}/whatsapp-marketing/broadcasts/${id}/retry-failed`,
    callParams
  );
}

export async function retryAllWhatsAppBroadcast(id: string) {
  const callParams = await getCallParams("POST", {});
  return makeCall(
    `${urls.url_prod}/whatsapp-marketing/broadcasts/${id}/retry-all`,
    callParams
  );
}

export async function rescheduleWhatsAppBroadcast(
  id: string,
  body: {
    scheduledAt?: string;
    timezone?: string;
    runNow?: boolean;
  }
) {
  // Use PUT — getCallParams historically lacked PATCH and fell through to GET
  const callParams = await getCallParams("PUT", {
    timezone: "Asia/Kolkata",
    ...body,
  });
  return makeCall(
    `${urls.url_prod}/whatsapp-marketing/broadcasts/${id}/schedule`,
    callParams
  );
}

/** Delete scheduled broadcast only (blocked if already sent / completed) */
export async function deleteScheduledWhatsAppBroadcast(id: string) {
  const callParams = await getCallParams("DELETE", {});
  return makeCall(
    `${urls.url_prod}/whatsapp-marketing/broadcasts/${id}`,
    callParams
  );
}
